/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { requestCurrentValueDataset } from '@/api/eeCheckApi';
import { useI18n } from '@/context/I18nContext';
import { useSession } from '@/context/SessionContext';
import { isExpiredSessionError } from '@/lib/app/debugRequests';
import { logApplicationError, logApplicationErrors } from '@/lib/app/errorLog';
import {
    getCurrentValueSensorNotFoundMessage,
    getCurrentValueSensorNotNumericMessage,
} from '@/lib/sensors/presentation/sensorRequestMessages';
import {
    collapseItems,
    createErrorItems,
    createResolvedConfigs,
    normalizeCalcValue,
    type Api2ValueDataCommonProps,
    type Api2ValueDataFlatItem,
    type Api2ValueDataItem,
    type Api2ValueDataRenderState,
} from '@/lib/overview/api2ValueData';
import { parseLastValueCsv } from '@/lib/timeSeries/parseLastValueCsv';
import {
    createTimeSeriesCalcValuesWithAliases,
    formatTimeSeriesNumericValue,
    normalizeTimeSeriesUrn,
} from '@/lib/timeSeries/timeSeriesValue';
import type { LastValueRequest } from '@/types/eeCheck';

interface Api2LastValueDataProps extends Omit<Api2ValueDataCommonProps, 'showDate'> {
    children: (state: Api2ValueDataRenderState) => ReactNode;
    date: string;
}

function logCurrentValueError(message: string): void {
    logApplicationError({
        message,
        source: 'Api2LastValueData.tsx:loadItems',
    });
}

function logCurrentValueErrors(messages: readonly string[]): void {
    logApplicationErrors(messages.map((message) => ({
        message,
        source: 'Api2LastValueData.tsx:loadItems',
    })));
}

function Api2LastValueData({
    calc,
    children,
    conversion,
    date,
    fractionDigits,
    offset,
    title,
    unit,
    urn,
}: Api2LastValueDataProps) {
    const { locale, t } = useI18n();
    const { accessToken, isSessionExpired, onSessionExpired } = useSession();
    const [items, setItems] = useState<Api2ValueDataItem[]>([]);
    const [completedRequestSignature, setCompletedRequestSignature] = useState<string | null>(null);
    const [loadingLabel, setLoadingLabel] = useState('');

    const resolvedConfigs = useMemo(() => {
        return createResolvedConfigs(
            'last',
            conversion,
            fractionDigits,
            offset,
            undefined,
            title,
            unit,
            urn,
        );
    }, [conversion, fractionDigits, offset, title, unit, urn]);
    const configsSignature = JSON.stringify(resolvedConfigs);
    const requestSignature = JSON.stringify({
        configs: configsSignature,
        date,
        accessToken,
    });
    const isLoading = Boolean(accessToken) && !isSessionExpired && completedRequestSignature !== requestSignature;

    useEffect(() => {
        if (isSessionExpired) {
            setCompletedRequestSignature(requestSignature);
            return;
        }

        if (!accessToken) {
            setItems([]);
            setCompletedRequestSignature(requestSignature);
            return;
        }

        if (resolvedConfigs.length === 0 || !date) {
            const errorMessage = t('Missing current value request parameters.');

            logCurrentValueError(errorMessage);
            setItems(
                createErrorItems(
                    resolvedConfigs,
                    errorMessage,
                ),
            );
            setCompletedRequestSignature(requestSignature);
            return;
        }

        const requestUrn = Array.from(
            new Set(
                resolvedConfigs
                    .map((config) => normalizeTimeSeriesUrn(config.urn))
                    .filter(Boolean),
            ),
        ).join(',');
        const payload: LastValueRequest = {
            urn: requestUrn,
            ...(date === 'NOW' ? {} : { timestamp: date }),
        };

        let isCancelled = false;

        async function loadItems(): Promise<void> {
            setLoadingLabel(t('Loading API request...'));

            try {
                const { data } = await requestCurrentValueDataset(accessToken, payload, ({ currentRequest, totalRequests }) => {
                    setLoadingLabel(
                        totalRequests > 1
                            ? t('Loading API request {current}/{total}', {
                                current: String(currentRequest),
                                total: String(totalRequests),
                            })
                            : t('Loading API request...'),
                    );
                });

                if (isCancelled) {
                    return;
                }

                if (typeof data !== 'string') {
                    const errorMessage = t('Current value response is not a CSV string.');

                    logCurrentValueError(errorMessage);
                    setItems(
                        createErrorItems(
                            resolvedConfigs,
                            errorMessage,
                        ),
                    );
                    return;
                }

                const parsedLastValue = parseLastValueCsv(data);
                const rowsByUrn = parsedLastValue.rows.reduce<Map<string, { timestamp: string; value: string }>>((result, row) => {
                    result.set(row.urn.trim(), {
                        timestamp: row.timestamp,
                        value: row.value,
                    });
                    return result;
                }, new Map());
                const currentValueErrors: string[] = [];

                const nextItems: Api2ValueDataFlatItem[] = resolvedConfigs.map((config) => {
                    const row = rowsByUrn.get(config.urn.trim());

                    if (!row) {
                        const errorMessage = getCurrentValueSensorNotFoundMessage(t, config.urn);

                        currentValueErrors.push(errorMessage);
                        return {
                            aggregation: config.aggregation,
                            date: null,
                            errorMessage,
                            formattedValue: null,
                            fractionDigits: config.fractionDigits,
                            groupIndex: config.groupIndex,
                            key: config.key,
                            rawValue: null,
                            title: config.title,
                            unit: config.unit,
                            urn: config.urn,
                            value: null,
                        };
                    }

                    if (row.value.trim() === '') {
                        const errorMessage = getCurrentValueSensorNotNumericMessage(t, config.urn);

                        currentValueErrors.push(errorMessage);
                        return {
                            aggregation: config.aggregation,
                            date: row.timestamp || null,
                            errorMessage,
                            formattedValue: null,
                            fractionDigits: config.fractionDigits,
                            groupIndex: config.groupIndex,
                            key: config.key,
                            rawValue: null,
                            title: config.title,
                            unit: config.unit,
                            urn: config.urn,
                            value: null,
                        };
                    }

                    const numericValue = Number(row.value);

                    if (!Number.isFinite(numericValue)) {
                        const errorMessage = getCurrentValueSensorNotNumericMessage(t, config.urn);

                        currentValueErrors.push(errorMessage);
                        return {
                            aggregation: config.aggregation,
                            date: row.timestamp || null,
                            errorMessage,
                            formattedValue: null,
                            fractionDigits: config.fractionDigits,
                            groupIndex: config.groupIndex,
                            key: config.key,
                            rawValue: null,
                            title: config.title,
                            unit: config.unit,
                            urn: config.urn,
                            value: null,
                        };
                    }

                    const convertedValue = numericValue * config.conversion + config.offset;

                    return {
                        aggregation: config.aggregation,
                        date: row.timestamp || null,
                        errorMessage: '',
                        formattedValue: formatTimeSeriesNumericValue(
                            convertedValue,
                            locale,
                            config.fractionDigits,
                        ),
                        fractionDigits: config.fractionDigits,
                        groupIndex: config.groupIndex,
                        key: config.key,
                        rawValue: numericValue,
                        title: config.title,
                        unit: config.unit,
                        urn: config.urn,
                        value: convertedValue,
                    };
                });

                logCurrentValueErrors(currentValueErrors);

                const computedItems = new Map<number, Api2ValueDataFlatItem[]>();

                nextItems.forEach((item) => {
                    const currentGroupItems = computedItems.get(item.groupIndex) ?? [];
                    currentGroupItems.push(item);
                    computedItems.set(item.groupIndex, currentGroupItems);
                });

                computedItems.forEach((groupItems, groupIndex) => {
                    const calcForGroup = normalizeCalcValue(calc, groupIndex);

                    if (!calcForGroup) {
                        return;
                    }

                    const values = groupItems.reduce<Record<string, number | null>>((result, item) => {
                        result[item.urn] = item.rawValue;
                        return result;
                    }, {});
                    const computedValues = calcForGroup({
                        values: createTimeSeriesCalcValuesWithAliases(values),
                    });
                    const firstGroupItem = groupItems[0];
                    const fallbackDate = groupItems.find((item) => item.date !== null)?.date ?? null;

                    Object.entries(computedValues).forEach(([computedKey, computedValue], computedIndex) => {
                        const formattedValue = computedValue === null
                            ? null
                            : formatTimeSeriesNumericValue(
                                computedValue,
                                locale,
                                firstGroupItem?.fractionDigits,
                            );

                        groupItems.push({
                            aggregation: firstGroupItem?.aggregation ?? 'last',
                            date: fallbackDate,
                            errorMessage: '',
                            formattedValue,
                            fractionDigits: firstGroupItem?.fractionDigits ?? 2,
                            groupIndex,
                            key: `${computedKey}-${groupIndex}-calc-${computedIndex}`,
                            rawValue: computedValue,
                            title: firstGroupItem?.title ?? null,
                            unit: null,
                            urn: computedKey,
                            value: computedValue,
                        });
                    });
                });

                setItems(collapseItems(Array.from(computedItems.values()).flat()));
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                if (isExpiredSessionError(error)) {
                    onSessionExpired();
                }

                const errorMessage = error instanceof Error
                    ? error.message
                    : t('Current value request failed.');

                logCurrentValueError(errorMessage);

                setItems(
                    createErrorItems(
                        resolvedConfigs,
                        errorMessage,
                    ),
                );
            } finally {
                if (!isCancelled) {
                    setCompletedRequestSignature(requestSignature);
                    setLoadingLabel('');
                }
            }
        }

        void loadItems();

        return () => {
            isCancelled = true;
        };
    }, [accessToken, calc, configsSignature, date, isSessionExpired, locale, onSessionExpired, requestSignature, resolvedConfigs, t]);

    return <>{children({ items, isLoading, loadingLabel })}</>;
}

export default Api2LastValueData;
